import { useLayoutEffect, useRef } from "react";

/**
 * Deterministic <head> writer.
 *
 * All mounted head specs live in one module-level registry and are composed
 * synchronously in useLayoutEffect (no requestAnimationFrame, no instances
 * leaked by discarded renders). Composition order is (priority, mount order),
 * so a route-level default (priority 0) is always overridden by a page-level
 * <Seo> (priority 1) regardless of which effect runs first.
 */
export type HeadTag = {
  tag: "meta" | "link" | "script";
  attrs: Record<string, string>;
  content?: string;
};

export interface HeadSpec {
  title?: string;
  lang?: string;
  tags: HeadTag[];
}

type Entry = { id: number; priority: number; spec: HeadSpec };

const entries = new Map<number, Entry>();
let seq = 0;

function selectorFor(t: HeadTag): string | null {
  if (t.tag === "meta") {
    // og:locale:alternate legitimately repeats per locale — dedupe on the pair
    // so route-level and page-level writers don't both emit the same alternate.
    if (t.attrs.property === "og:locale:alternate") {
      return `meta[property="og:locale:alternate"][content="${CSS.escape(t.attrs.content ?? "")}"]`;
    }
    if (t.attrs.name) return `meta[name="${CSS.escape(t.attrs.name)}"]`;
    if (t.attrs.property) return `meta[property="${CSS.escape(t.attrs.property)}"]`;
    return null;
  }
  if (t.tag === "link") {
    const rel = t.attrs.rel;
    if (rel === "canonical") return 'link[rel="canonical"]';
    if (rel === "alternate" && t.attrs.hreflang) {
      return `link[rel="alternate"][hreflang="${CSS.escape(t.attrs.hreflang)}"]`;
    }
    return null;
  }
  // JSON-LD blocks stack (sitewide Organization/WebSite + per-route schemas).
  return null;
}

function create(t: HeadTag) {
  const el = document.createElement(t.tag);
  for (const [k, v] of Object.entries(t.attrs)) el.setAttribute(k, v);
  if (t.content != null) el.textContent = t.content;
  el.setAttribute("data-bp-head", "1");
  document.head.appendChild(el);
}

function apply() {
  if (typeof document === "undefined") return;
  document.head.querySelectorAll("[data-bp-head]").forEach((n) => n.remove());

  const sorted = [...entries.values()].sort((a, b) => a.priority - b.priority || a.id - b.id);

  let title: string | undefined;
  let lang: string | undefined;
  const keyed = new Map<string, HeadTag>();
  const stacked: HeadTag[] = [];

  for (const e of sorted) {
    if (e.spec.title) title = e.spec.title;
    if (e.spec.lang) lang = e.spec.lang;
    for (const t of e.spec.tags) {
      const sel = selectorFor(t);
      if (sel) keyed.set(sel, t);
      else stacked.push(t);
    }
  }

  if (title) document.title = title;
  if (lang) document.documentElement.lang = lang;

  for (const [sel, t] of keyed) {
    // Drop the static index.html fallback so crawlers never see two values.
    document.head.querySelectorAll(sel).forEach((n) => {
      if (!n.hasAttribute("data-bp-head")) n.remove();
    });
    create(t);
  }
  for (const t of stacked) create(t);
}

/**
 * @param spec  title / lang / tags to contribute to the document head.
 * @param priority higher wins. 0 = route-level default, 1 = page-level.
 */
export function useHead(spec: HeadSpec, priority = 1) {
  const idRef = useRef<number>();
  if (idRef.current === undefined) idRef.current = ++seq;
  const id = idRef.current;
  // Serialize so the effect re-runs on any content change (including a
  // language switch that changes every string) but not on unrelated renders.
  const key = JSON.stringify(spec);

  useLayoutEffect(() => {
    entries.set(id, { id, priority, spec });
    apply();
    return () => {
      entries.delete(id);
      apply();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, id, priority]);
}
